<script setup lang="ts">
import { computed, ref } from 'vue'
import { HoverCardArrow, HoverCardContent, HoverCardPortal, HoverCardRoot, HoverCardTrigger } from 'reka-ui'
import LockedOption from '@/components/LockedOption.vue'
import { useUserStore } from '@/authentication/user.store'
import { toolkitUrls } from '@/util/links'
import { truncateTextWithEllipsis } from '@/util/strings'
import type { UdlCheckpoint, UdlStrategy } from './udl_index_types'
import SlidePanel from '@/components/SlidePanel.vue'

const PREVIEW_DESCRIPTION_LENGTH = 140
const PREVIEW_OPEN_DELAY_MS = 0
// long enough for the pointer to cross the gap onto the preview, so it can be hovered (WCAG 1.4.13)
const PREVIEW_CLOSE_DELAY_MS = 200

const PRINCIPLE_LABEL_CLASSES: Record<string, string> = {
  Representation: 'bg-udl-representationDark',
  Action: 'bg-udl-expressionDark',
  Engagement: 'bg-udl-engagementDark'
}

const props = withDefaults(
  defineProps<{
    checkpoint: UdlCheckpoint
    principle?: string
    expanded?: boolean
  }>(),
  { principle: '', expanded: true }
)

const emit = defineEmits<{
  'update:expanded': [checkpointRef: string, expanded: boolean]
}>()

const userStore = useUserStore()

const previewedStrategy = ref('')

const panelId = computed(() => `udl-checkpoint-${props.checkpoint.standard_long_ref.replace(/\W+/g, '-').toLowerCase()}`)
const principleLabelClass = computed(() => PRINCIPLE_LABEL_CLASSES[props.principle.split(' ')[0]] || '')
const strategies = computed(() => props.checkpoint.adaptations)

function isUnlocked(strategy: UdlStrategy) {
  return userStore.isPremium || Boolean(strategy.demo)
}

function previewId(strategy: UdlStrategy) {
  return `${panelId.value}-preview-${strategy.uuid}`
}

function setPreviewOpen(strategy: UdlStrategy, open: boolean) {
  if (open) {
    previewedStrategy.value = strategy.uuid
  } else if (previewedStrategy.value === strategy.uuid) {
    previewedStrategy.value = ''
  }
}

function toggleExpanded() {
  emit('update:expanded', props.checkpoint.standard_long_ref, !props.expanded)
}
</script>

<template>
  <div>
    <h5 class="mb-2.5 mt-0 text-text-md font-regular leading-[1.61em]">
      <button
        type="button"
        class="text-left text-gray-900 hover:underline focus:outline-offset-4"
        :aria-expanded="expanded"
        :aria-controls="panelId"
        @click="toggleExpanded"
      >
        <span
          v-if="principle"
          class="mr-1 inline-block rounded-xs px-2 pb-1 pt-0.5 text-text-md font-bold leading-none text-white"
          :class="principleLabelClass"
        >
          {{ checkpoint.udl_checkpoint }}
        </span>
        <span aria-hidden="true">{{ expanded ? '–' : '+' }}</span>
        {{ checkpoint.udl_checkpoint_name }}
        <span class="text-gray-700">({{ strategies.length }} strategies)</span>
      </button>
    </h5>

    <SlidePanel :expanded="expanded">
      <ul :id="panelId" class="mb-4 mt-1 list-disc pl-11 leading-5 text-gray-500">
      <li
        v-for="strategy in strategies"
        :key="strategy.uuid"
        class="mb-1"
        :class="{ '-ml-1 list-none': !isUnlocked(strategy) }"
      >
        <HoverCardRoot
          v-if="isUnlocked(strategy)"
          :open="previewedStrategy === strategy.uuid"
          :open-delay="PREVIEW_OPEN_DELAY_MS"
          :close-delay="PREVIEW_CLOSE_DELAY_MS"
          @update:open="(open) => setPreviewOpen(strategy, open)"
        >
          <HoverCardTrigger as-child>
            <a
              :href="toolkitUrls.strategyPage(strategy.slug)"
              target="_blank"
              rel="noopener"
              class="text-blue-400 hover:underline focus:outline-offset-4"
              :aria-describedby="previewedStrategy === strategy.uuid ? previewId(strategy) : undefined"
            >
              {{ strategy.title }}
              <span class="sr-only">(Opens in a new tab)</span>
            </a>
          </HoverCardTrigger>
          <HoverCardPortal>
            <HoverCardContent
              data-testid="strategy-preview"
              side="right"
              :side-offset="10"
              class="z-50 w-[276px] rounded-sm border border-black/20 bg-white text-text-md shadow-[0_5px_10px_rgba(0,0,0,0.2)]"
            >
              <p class="m-0 rounded-t-sm border-b border-gray-200 bg-gray-100 px-3.5 py-2 font-medium text-gray-700">
                {{ strategy.title }}
              </p>
              <div :id="previewId(strategy)" class="px-3.5 py-2 text-gray-900">
                <img
                  :src="strategy.image_url"
                  alt=""
                  aria-hidden="true"
                  class="mb-2.5 max-h-[120px] rounded-xs border border-gray-300 p-1"
                />
                <p class="m-0">{{ truncateTextWithEllipsis(strategy.description, PREVIEW_DESCRIPTION_LENGTH) }}</p>
              </div>
              <HoverCardArrow :width="20" :height="10" class="fill-white drop-shadow-[-1px_0_0_rgba(0,0,0,0.2)]" />
            </HoverCardContent>
          </HoverCardPortal>
        </HoverCardRoot>
        <LockedOption v-else :label="strategy.title" hanging />
      </li>
      </ul>
    </SlidePanel>
  </div>
</template>
